import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function PortalTicketDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: t, isLoading } = useQuery({ queryKey: ["portal-ticket", id], queryFn: async () => (await api.get(`/portal/tickets/${id}`)).data, retry: false });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const reply = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/portal/tickets/${id}/reply`, { message: msg });
      setMsg("");
      qc.invalidateQueries({ queryKey: ["portal-ticket", id] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (isLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!t) return <p className="text-muted-foreground">Ticket not found.</p>;

  return (
    <div>
      <Link to="/dashboard/tickets" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-1.5 h-4 w-4" /> Tickets</Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><span className="font-mono text-xs text-muted-foreground">{t.ticket_no}</span><h1 className="font-display text-2xl font-bold">{t.subject}</h1></div>
        <div className="flex gap-2"><Badge variant="outline">{t.category}</Badge><Badge variant="secondary">{t.status}</Badge></div>
      </div>

      <div className="mt-6 space-y-4">
        {(t.messages || []).filter((m) => !m.internal).map((m, i) => (
          <div key={i} className={`max-w-[85%] rounded-2xl border border-border p-4 ${m.author_role === "client" ? "ml-auto bg-primary/10" : "bg-card"}`}>
            <p className="text-xs font-semibold text-muted-foreground">{m.author_name} · {m.author_role === "client" ? "You" : "Buddy Solution"}</p>
            <p className="mt-1.5 text-sm">{m.body}</p>
            <p className="mt-2 text-[11px] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</p>
          </div>
        ))}
      </div>

      {t.status !== "Closed" && (
        <form onSubmit={reply} className="mt-6 flex items-end gap-3" data-testid="ticket-reply-form">
          <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type your reply..." className="flex-1" data-testid="ticket-reply-input" />
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="ticket-reply-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        </form>
      )}
    </div>
  );
}
